import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { CookieService } from '~/common/utils/cookies';
import type { ConsentState } from '~/common/constants/consent';
import {
  COOKIE_PREFERENCE_CATEGORY_IDS,
  COOKIE_PREFERENCE_CATEGORY_COPY,
  COOKIE_PREFERENCE_RELOAD_NOTICE,
} from '~/common/constants/cookiePreferences';
import Toggle from '~/components/Toggle';
import AccordionMinus from '~/components/icons/AccordionMinus';
import AccordionPlus from '~/components/icons/AccordionPlus';
import ModalClose from '~/components/icons/ModalClose';
import {
  Overlay,
  Modal,
  Header,
  HeaderText,
  Title,
  Subtitle,
  CloseButton,
  CategoryList,
  CategoryItem,
  CategoryTextColumn,
  AccordionTrigger,
  CategoryToggleColumn,
  CategoryTitleRow,
  AccordionIcon,
  CategoryTitle,
  AccordionContent,
  AccordionContentInner,
  CategoryDescription,
  ReloadNotice,
  SaveButton,
} from './styled';

export type CookiePreferencesPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    state: Pick<ConsentState, 'analytics' | 'functionality' | 'marketing'>,
  ) => void;
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const CookiePreferencesPanel = ({
  isOpen,
  onClose,
  onSave,
}: CookiePreferencesPanelProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  const [analytics, setAnalytics] = useState(false);
  const [functionality, setFunctionality] = useState(false);
  const [marketing, setMarketing] = useState(false);
  /**
   * Marketing consent as it stood when the panel opened. Only a granted → withdrawn
   * transition reloads the page, so this is what decides whether the refresh notice is
   * shown — keying off live state alone would show it to first-time visitors, who have
   * nothing loaded to unload.
   */
  const [initialMarketing, setInitialMarketing] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const existing = CookieService.getConsent();

    if (existing && !CookieService.isConsentExpired(existing)) {
      setAnalytics(existing.analytics);
      setFunctionality(existing.functionality);
      setMarketing(existing.marketing);
      setInitialMarketing(existing.marketing);
    } else {
      setAnalytics(false);
      setFunctionality(false);
      setMarketing(false);
      setInitialMarketing(false);
    }
    setExpandedCategory('strictly_necessary');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !overlayRef.current || !modalRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.25, ease: 'power2.out' },
      );
      gsap.fromTo(
        modalRef.current,
        { scale: 0.96, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.3, ease: 'power3.out' },
      );
    });

    return () => ctx.revert();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const focusable =
      modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusable[0];

    if (first) {
      (first as HTMLElement).focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusable = Array.from(
        modalRef.current!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !(el as HTMLButtonElement).disabled);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = () => {
    if (!modalRef.current || !overlayRef.current) {
      onClose();
      previousActiveRef.current?.focus();
      return;
    }

    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
    });
    gsap.to(modalRef.current, {
      scale: 0.96,
      opacity: 0,
      duration: 0.2,
      ease: 'power3.in',
      onComplete: () => {
        onClose();
        previousActiveRef.current?.focus();
      },
    });
  };

  const handleSave = () => {
    onSave({ analytics, functionality, marketing });
    handleClose();
  };

  const toggleExpanded = (id: string) => {
    setExpandedCategory((prev) => (prev === id ? null : id));
  };

  if (!isOpen) return null;

  return (
    <Overlay
      ref={overlayRef}
      role="dialog"
      aria-labelledby="cookie-settings-title"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <Modal ref={modalRef}>
        <Header>
          <HeaderText>
            <Title id="cookie-settings-title">COOKIE SETTINGS</Title>
            <Subtitle>MANAGE YOUR COOKIE SETTINGS HERE.</Subtitle>
          </HeaderText>
          <CloseButton
            type="button"
            onClick={handleClose}
            aria-label="Close cookie settings"
          >
            <ModalClose />
          </CloseButton>
        </Header>

        <CategoryList>
          {COOKIE_PREFERENCE_CATEGORY_IDS.map((id) => {
            const isStrictlyNecessary = id === 'strictly_necessary';
            const isExpanded = expandedCategory === id;
            const checked = isStrictlyNecessary
              ? true
              : id === 'analytics'
                ? analytics
                : id === 'functionality'
                  ? functionality
                  : marketing;
            const setChecked =
              id === 'analytics'
                ? setAnalytics
                : id === 'functionality'
                  ? setFunctionality
                  : setMarketing;

            return (
              <CategoryItem key={id}>
                <CategoryTextColumn>
                  <AccordionTrigger
                    type="button"
                    onClick={() => toggleExpanded(id)}
                    aria-expanded={isExpanded}
                    aria-controls={`${id}-description`}
                  >
                    <CategoryTitleRow>
                      <AccordionIcon $expanded={isExpanded}>
                        {isExpanded ? <AccordionMinus /> : <AccordionPlus />}
                      </AccordionIcon>
                      <CategoryTitle id={`${id}-title`}>
                        {COOKIE_PREFERENCE_CATEGORY_COPY[id].title}
                      </CategoryTitle>
                    </CategoryTitleRow>
                  </AccordionTrigger>
                  <AccordionContent
                    id={`${id}-description`}
                    $expanded={isExpanded}
                    role="region"
                    aria-labelledby={`${id}-title`}
                  >
                    <AccordionContentInner>
                      <CategoryDescription>
                        {COOKIE_PREFERENCE_CATEGORY_COPY[id].description}
                      </CategoryDescription>
                    </AccordionContentInner>
                  </AccordionContent>
                </CategoryTextColumn>
                <CategoryToggleColumn>
                  <Toggle
                    checked={checked}
                    disabled={isStrictlyNecessary}
                    onChange={() => setChecked((v) => !v)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`${COOKIE_PREFERENCE_CATEGORY_COPY[id].title} cookies`}
                  />
                </CategoryToggleColumn>
              </CategoryItem>
            );
          })}
        </CategoryList>

        {initialMarketing && !marketing && (
          <ReloadNotice>{COOKIE_PREFERENCE_RELOAD_NOTICE}</ReloadNotice>
        )}

        <SaveButton type="button" onClick={handleSave}>
          SAVE PREFERENCES
        </SaveButton>
      </Modal>
    </Overlay>
  );
};

export default CookiePreferencesPanel;
